import { Link } from "react-router-dom";
import { COLORS } from "../hooks/useAudioRecorder";
import TradeLinks from "./TradeLinks";

/**
 * "What is HandInvoice" — supporting copy below the Home tutorial cards.
 *
 * Two jobs: give crawlers and AI answer engines real, indexable prose about
 * what HandInvoice is (a bare hero + button ranks poorly), and give genuine
 * first-time visitors more context than the sparse hero offers. Kept small
 * and direct — no marketing puff — so it doesn't fight the record button or
 * break the industrial aesthetic.
 */
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const SERIF = "'Instrument Serif', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";
const INK_DIM = "#a8a29a";

export default function WhatIsHandInvoice() {
  return (
    <section
      data-testid="home-what-is-handinvoice"
      style={{
        maxWidth: 560,
        margin: "0 auto",
        padding: "20px 24px 8px",
      }}
    >
      <h2
        style={{
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: COLORS.faint,
          margin: "0 0 20px 0",
          paddingBottom: 8,
          borderBottom: `1px dashed ${COLORS.rule}`,
        }}
      >
        // what is HandInvoice
      </h2>

      <Block title="Speak it. Send it.">
        HandInvoice turns a voice note into a finished invoice. Instead of typing
        line items into a form on a job site, you tap record and describe the
        work the way you'd tell a coworker — who it was for, how long it took,
        what parts you used, whatever tax applies. HandInvoice listens, pulls
        out the details, and builds a clean, professional PDF you can hand to
        the customer. It's made for the people who do the work: plumbers,
        electricians, HVAC techs, handymen, painters, landscapers, freelancers
        — anyone who invoices for their own time. There's no signup wall, no
        app to download, and no spreadsheet to wrestle. Bookmark the page, and
        the next invoice is about sixty seconds of talking away.
      </Block>

      <Block title="How it works">
        Three steps. First, set up your business once in Settings — your name,
        contact details, license number, and any default tax. That information
        goes on every invoice so you never retype it. Second, tap the record
        button and talk: describe the customer, your hours or flat rate,
        materials and where you bought them, and any fees or tax. Speak
        naturally — pauses are fine. Third, review. A language model
        structures what you said into proper line items and totals, and the
        Review screen shows you every field before anything is final. Nothing
        is locked in — correct a number, fix a name, add a charge. When it
        looks right, download the PDF. You are always the last check, never
        the AI.
      </Block>

      <Block title="Privacy by default">
        HandInvoice is built to keep as little as possible. Your audio
        recording is never saved — it stays in memory only while you're
        recording and is deleted the moment your PDF is generated. Use
        HandInvoice without signing in and nothing about you is written to our
        servers at all; your business details live in your browser and nothing
        else leaves the page. Sign in, and your invoices are saved to your
        account — visible only to you, never sold, never shared for
        advertising.{" "}
        <Link
          to="/privacy"
          style={{ color: COLORS.yellow, textDecoration: "none" }}
        >
          Read the full privacy policy
        </Link>{" "}
        for exactly what is and isn't collected, in plain language.
      </Block>

      <TradeLinks />
    </section>
  );
}

const Block = ({ title, children }) => (
  <div style={{ marginBottom: 22 }}>
    <h3
      style={{
        fontFamily: SERIF,
        fontStyle: "italic",
        fontSize: 22,
        color: COLORS.text,
        margin: "0 0 6px 0",
      }}
    >
      {title}
    </h3>
    <p
      style={{
        fontFamily: SANS,
        fontSize: 14,
        lineHeight: 1.7,
        color: INK_DIM,
        margin: 0,
      }}
    >
      {children}
    </p>
  </div>
);
